
































import { Component, Vue } from 'vue-property-decorator';

import PlusCircleIcon from '@/components/icons/PlusCircle.vue';
import Page from '@/componentsV2/molecules/Page/Page.vue';
import Typography from '@/componentsV2/atoms/Typography/Typography.vue';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import { ModalOptionsV2 } from '@/components/Modal/ModalOptions';

import LocationsService from '@/api/LocationsService';
import SpecialCoinOffersService from '@/api/SpecialCoinOffersService';

import { ILocation } from '@/models/location';
import { IOfferListItem } from '@/models/offer';

import { IInternationalization } from '@/models/internationalization';

import eventBus from '@/utils/eventBus';
import { getBillingIsHidden } from '@/utils/billing';

import flagKeys from '@/constants/flagKeys';

import LocationList, { Location as LocationItem } from './LocationList.vue';
import { LocationFormModalMethod } from '@/componentsV2/templates/Modals/LocationFormModal/Steps/AddLocation.vue';
import { EBillingStatusTypes } from '@/store/modules/businessTypes';
import BillingService from '@/api/BillingService';
import LocationEmptyState from './LocationEmptyState.vue';
import { IBusinessType } from '@/models/business';

function locationFactory() {
  return {
    address: '',
    city: '',
    zip: '',
    state: '',
    phone: '',
    phoneCountryCode: '',
    phoneCountryIso: '',
    primary: false,
    addressAdditional: '',
    merchantId: '',
    fullAddress: ''
  };
}

@Component({
  components: {
    Page,
    Typography,
    FilledButton,
    LocationList,
    PlusCircleIcon,
    LocationEmptyState
  }
})
export default class ManageLocations extends Vue {
  locationService = new LocationsService();
  offerService = new SpecialCoinOffersService();
  billingService = new BillingService();

  loading = false;
  locations: LocationItem[] = [];
  offers: IOfferListItem[] = [];

  get tPrefix() {
    return 'business.locations';
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  // Guards only the second half of `handleLocationRemoved` — the billing-overview
  // refetch and the "your plan has been updated" modal. The block above it is the
  // online↔physical type transition (TWISM-18944): the type reversion survives the
  // flag, only its return-online plan modal is billing UI (TWISM-19012).
  get subscribedBilling() {
    if (getBillingIsHidden()) return false;

    return this.billingStatus === EBillingStatusTypes.SUBSCRIBED;
  }

  // The UNAVAILABLE online→both branch keeps converted merchants on the empty
  // state as an upgrade pitch. With billing hidden there is nothing to upgrade
  // to — a merchant with locations goes straight to the list (TWISM-19139).
  get emptyState() {
    return (
      (!this.locations.length ||
        (!getBillingIsHidden() &&
          this.billingStatus === EBillingStatusTypes.UNAVAILABLE &&
          this.businessInfo.firstType === IBusinessType.ONLINE &&
          this.businessInfo.type === IBusinessType.BOTH)) &&
      !this.loading
    );
  }

  get textTitle() {
    if (this.emptyState) return 'Manage Locations';
    return this.$t(`${this.tPrefix}.title`);
  }

  get delayedPeriod() {
    if (getBillingIsHidden()) return false;

    return (
      this.billingModuleV3 &&
      this.billingStatus === EBillingStatusTypes.DELAY_PERIOD
    );
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessState'] || {};
  }

  get billingStatus() {
    return this.businessInfo.subscriptionStatus;
  }

  get billingModuleV3() {
    return this.flags.includes(flagKeys.billingModuleV3);
  }

  created() {
    eventBus.$on('reloadLocations', this.loadLocations);
  }

  beforeDestroy() {
    eventBus.$off('reloadLocations', this.loadLocations);
  }

  mounted() {
    this.loadLocations();
    this.loadOffers();
  }

  handleOpenSideModal(type: 'create' | 'edit', data: any) {
    const modalConfig = {
      isOpen: true,
      component: 'locationsDetails',
      bgColor: 'var(--color-white)',
      minWidth: '512px',
      activePointerEvents: 'auto',
      data: {
        type,
        location: Object.assign({}, data)
      }
    };

    eventBus.$emit('setSideModalVisible', modalConfig);
  }

  handleEditLocation(location: ILocation) {
    this.handleOpenLocationFormModal('edit', location);
  }

  handleOpenLocationFormModal(
    method: LocationFormModalMethod,
    location?: ILocation
  ) {
    eventBus.$emit(
      'openSubModal',
      new ModalOptionsV2({
        icon: 'none',
        hideCancelButton: true,
        confirmEvent: '',
        componentToRender: 'locationFormModal'
      }),
      {
        ...(location && { location }),
        method,
        offers: this.offers,
        reloadLocations: () => {
          this.loadLocations({ showLoader: false });
        }
      }
    );
  }

  handleAddLocation() {
    this.handleOpenLocationFormModal('create');
  }

  getCountryPhoneMask(location: ILocation) {
    if (location && location.phoneCountryIso) {
      const sessionCountries: IInternationalization[] = this.$store.getters[
        'internationalization/getCountries'
      ];

      const locationCountry = sessionCountries.find(
        country => country.countryIso2 === location.phoneCountryIso
      );

      if (locationCountry && locationCountry.phoneMask) {
        return locationCountry.phoneMask;
      }
    }

    return 'XXXXXXXXXX';
  }

  async handleLocationRemoved(locationId: string) {
    this.locations = this.locations.filter(
      location => location.id !== locationId
    );

    if (
      this.locations.length === 0 &&
      this.flags.includes(flagKeys.billingModuleV4) &&
      this.businessInfo.firstType !== this.businessInfo.type
    ) {
      this.$store.dispatch('business/SET_BUSINESS_INFO', {
        ...this.businessInfo,
        type: this.businessInfo.firstType || this.businessInfo.type,
        subscriptionStatus: EBillingStatusTypes.UNAVAILABLE
      });

      // The type reversion above mirrors the server — the business is
      // online-only again either way. The modal is billing UI: it announces
      // the Coin Network plan subscription ending. With billing hidden the
      // conversion never carried a plan, so there is nothing to announce
      // (TWISM-19012).
      if (!getBillingIsHidden()) {
        setTimeout(() => {
          eventBus.$emit(
            'openSubModal',
            new ModalOptionsV2({
              componentToRender: `coinNetworkReturnOnlineModal`,
              hideCancelButton: false,
              icon: '',
              mobileFullScreen: true
            })
          );
        });
      }
      return;
    }

    if (this.subscribedBilling && !this.delayedPeriod) {
      try {
        this.$showLoaderWithLocker();

        const {
          data
        } = await this.billingService.getBillingOverviewSubscription();

        this.$store.dispatch('business/SET_BILLING_SUBSCRIPTION', data);

        eventBus.$emit(
          'openSubModal',
          new ModalOptionsV2({
            componentToRender: `billingLocationUpdatedModal`,
            hideCancelButton: false,
            icon: '',
            mobileFullScreen: true
          }),
          {
            action: 'delete',
            subscription: data
          }
        );
      } finally {
        this.$hideLoader();
      }
    }
  }

  formattedLocations(locations: any[]): LocationItem[] {
    return locations.map((location, index) => ({
      ...location,
      name: location.name || '',
      cityAndState: `${location.city} - ${location.state}`,
      phoneMask: this.getCountryPhoneMask(location),
      allowDeleteLocation:
        index !== 0 ||
        locations.length > 1 ||
        (this.businessInfo.type !== IBusinessType.ONLINE &&
          this.businessInfo.firstType === IBusinessType.ONLINE)
    }));
  }

  async loadLocations({ showLoader = true, loading = true } = {}) {
    if (showLoader) {
      this.$showLoaderWithLocker();
    }

    try {
      this.loading = loading;
      const { data } = await this.locationService.getLocations();

      this.locations = this.formattedLocations(data);
    } finally {
      this.loading = false;
      this.$hideLoader();
    }
  }

  async loadOffers() {
    const { status, data } = await this.offerService.getOffersByMethod([
      'offline',
      'offline_online'
    ]);
    this.offers = data.items;
  }
}
